import { z } from 'zod';

// Shared between the browser app, Pages Functions and Node scripts.

const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Expected a #RRGGBB color');
const id = z.string().regex(/^[a-z0-9][a-z0-9-]*$/, 'Use lowercase letters, numbers and dashes');
const relPath = z
  .string()
  .min(1)
  .refine((p) => !p.startsWith('/') && !p.includes('..') && !/^[a-z]+:/i.test(p), 'Must be a relative path inside the tenant folder');
const zip5 = z.string().regex(/^\d{5}$/, 'Enter a 5-digit ZIP code');
const point = z.tuple([z.number(), z.number()]);

function uniqueIds<T extends { id: string }>(items: T[], ctx: z.RefinementCtx, label: string) {
  const seen = new Set<string>();
  items.forEach((item, i) => {
    if (seen.has(item.id)) ctx.addIssue({ code: 'custom', message: `Duplicate ${label} id "${item.id}"`, path: [i, 'id'] });
    seen.add(item.id);
  });
}

export const colorSchema = z.object({
  id,
  name: z.string().min(1),
  texture: relPath,
  thumb: relPath,
});

export const finishSchema = z.enum(['high-gloss', 'gloss', 'satin', 'matte']);

export const systemSchema = z.object({
  id,
  name: z.string().min(1),
  description: z.string(),
  finish: finishSchema,
  tileInches: z.number().positive(),
  // Starting pattern size for this system (the customer can change it); default 2.
  patternScale: z.number().min(0.5).max(50).optional(),
  // How the texture tiles: "mirror" (default) hides seams in swatch photos;
  // "repeat" suits seamless textures with large patterns, where mirroring shows;
  // "stochastic" randomizes the tiling so random patterns (flake) never visibly repeat.
  wrap: z.enum(['mirror', 'repeat', 'stochastic']).default('mirror'),
  colors: z.array(colorSchema).min(1).superRefine((c, ctx) => uniqueIds(c, ctx, 'color')),
});

export const spaceTypeSchema = z.object({
  id,
  label: z.string().min(1),
  widthFt: z.number().positive(),
  depthFt: z.number().positive(),
});

export const sampleSchema = z.object({
  id,
  label: z.string().min(1),
  spaceType: id,
  image: relPath,
  mask: relPath,
  quad: z.tuple([point, point, point, point]),
});

const emails = z.array(z.email()).min(1);

export const territorySchema = z.object({
  id,
  name: z.string().min(1),
  zips: z.array(zip5).default([]),
  zipPrefixes: z.array(z.string().regex(/^\d{1,4}$/, 'ZIP prefix must be 1 to 4 digits')).default([]),
  emails,
});

export const routingSchema = z.object({
  mainOffice: z.object({ name: z.string().min(1), emails }),
  territories: z.array(territorySchema).superRefine((t, ctx) => uniqueIds(t, ctx, 'territory')),
});

export const tenantConfigSchema = z
  .object({
    slug: id,
    brand: z.object({
      name: z.string().min(1),
      logo: relPath,
      primary: hexColor,
      primaryText: hexColor,
      accent: hexColor,
      font: z.string().min(1),
      phone: z.string().min(1),
      website: z.url(),
    }),
    copy: z.object({
      headline: z.string().min(1),
      subhead: z.string().min(1),
      disclaimer: z.string().min(1),
      consent: z.string().min(1),
      privacyUrl: z.string().min(1),
    }),
    systems: z.array(systemSchema).min(1).superRefine((s, ctx) => uniqueIds(s, ctx, 'system')),
    spaceTypes: z.array(spaceTypeSchema).min(1).superRefine((s, ctx) => uniqueIds(s, ctx, 'space type')),
    sizeRanges: z.array(z.string().min(1)).min(1),
    bestTimes: z.array(z.string().min(1)).min(1),
    samples: z.array(sampleSchema).max(3).superRefine((s, ctx) => uniqueIds(s, ctx, 'sample')),
    // Coating carried up the wall base (stem wall / cove). Present = offered: uploads also
    // detect walls (a second detection per photo), and customers can switch it on and off.
    stemWall: z
      .object({
        heightInches: z.number().positive().max(24).default(4),
        defaultOn: z.boolean().default(true),
      })
      .optional(),
    // Logo drawn into every preview, centered on the floor (hard to crop out
    // without cropping the floor). `size` is its width as a share of the floor's width.
    watermark: z
      .object({
        image: relPath,
        opacity: z.number().min(0.05).max(1).default(0.35),
        size: z.number().min(0.1).max(1).default(0.45),
      })
      .optional(),
    routing: routingSchema,
    email: z.object({
      fromName: z.string().min(1),
      replyTo: z.email(),
    }),
  })
  .superRefine((cfg, ctx) => {
    const spaceIds = new Set(cfg.spaceTypes.map((s) => s.id));
    cfg.samples.forEach((s, i) => {
      if (!spaceIds.has(s.spaceType)) {
        ctx.addIssue({ code: 'custom', message: `Unknown spaceType "${s.spaceType}"`, path: ['samples', i, 'spaceType'] });
      }
    });
  });

export type TenantConfig = z.infer<typeof tenantConfigSchema>;
export type FloorSystem = z.infer<typeof systemSchema>;
export type FloorColor = z.infer<typeof colorSchema>;
export type SpaceType = z.infer<typeof spaceTypeSchema>;
export type Sample = z.infer<typeof sampleSchema>;
export type Finish = z.infer<typeof finishSchema>;
export type Wrap = FloorSystem['wrap'];
export type Quad = z.infer<typeof sampleSchema>['quad'];

/** Every file a tenant config references, relative to the tenant folder. */
export function tenantFiles(cfg: TenantConfig): string[] {
  const files = [cfg.brand.logo];
  for (const s of cfg.systems) for (const c of s.colors) files.push(c.texture, c.thumb);
  for (const s of cfg.samples) files.push(s.image, s.mask);
  if (cfg.watermark) files.push(cfg.watermark.image);
  return files;
}

// ---------------------------------------------------------------------------
// Lead payloads

/** Strips formatting from a US phone number and returns E.164, or null. */
export function toE164(raw: string): string | null {
  let digits = raw.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  return digits.length === 10 ? `+1${digits}` : null;
}

const phone = z
  .string()
  .trim()
  .transform((v, ctx) => {
    const e164 = toE164(v);
    if (!e164) {
      ctx.addIssue({ code: 'custom', message: 'Enter a 10-digit phone number' });
      return z.NEVER;
    }
    return e164;
  });

const email = z.string().trim().pipe(z.email({ message: 'Enter a valid email address' }));
const consent = z.literal(true, { message: 'Please check the box to continue' });
const uploadId = z.string().regex(/^([A-Za-z0-9_-]{21}|sample:[a-z0-9][a-z0-9-]*)$/, 'Invalid upload id');

const leadBase = {
  tenant: id,
  uploadId,
  systemId: id,
  colorId: id,
  turnstileToken: z.string().max(4096),
  website: z.string().max(200), // honeypot; checked by the server before validation
  sourceUrl: z.string().max(2048).optional(),
  utm: z.record(z.string().regex(/^utm_[a-z_]+$/), z.string().max(200)).optional(),
};

export const quoteFieldsSchema = z.object({
  name: z.string().trim().min(1, 'Enter your name').max(120),
  phone,
  email,
  zip: zip5,
  spaceType: id,
  sizeRange: z.string().min(1, 'Choose a size').max(100),
  bestTime: z.string().min(1, 'Choose a time').max(100),
  notes: z.string().trim().max(1000, 'Keep notes under 1000 characters').optional(),
  consent,
});

export const previewFieldsSchema = z.object({
  email,
  firstName: z.string().trim().max(60).optional(),
  zip: z.union([zip5, z.literal('')]).optional().transform((v) => v || undefined),
  consent,
});

export const quoteLeadSchema = quoteFieldsSchema.extend({ kind: z.literal('quote'), ...leadBase });
export const previewLeadSchema = previewFieldsSchema.extend({ kind: z.literal('preview_saved'), ...leadBase });
export const leadPayloadSchema = z.discriminatedUnion('kind', [quoteLeadSchema, previewLeadSchema]);

export type LeadPayload = z.infer<typeof leadPayloadSchema>;
export type LeadPayloadInput = z.input<typeof leadPayloadSchema>;
export type QuoteLead = z.infer<typeof quoteLeadSchema>;
export type PreviewLead = z.infer<typeof previewLeadSchema>;

/** Checks the parts of a lead that depend on the tenant config. Returns error messages keyed by field. */
export function checkLeadAgainstTenant(lead: LeadPayload, cfg: TenantConfig): Record<string, string> {
  const errors: Record<string, string> = {};
  if (lead.tenant !== cfg.slug) errors.tenant = 'Tenant mismatch';
  const system = cfg.systems.find((s) => s.id === lead.systemId);
  if (!system) errors.systemId = 'Unknown system';
  else if (!system.colors.some((c) => c.id === lead.colorId)) errors.colorId = 'Unknown color';
  if (lead.kind === 'quote') {
    if (!cfg.spaceTypes.some((s) => s.id === lead.spaceType)) errors.spaceType = 'Unknown space type';
    if (!cfg.sizeRanges.includes(lead.sizeRange)) errors.sizeRange = 'Choose a size';
    if (!cfg.bestTimes.includes(lead.bestTime)) errors.bestTime = 'Choose a time';
  }
  if (lead.uploadId.startsWith('sample:') && !cfg.samples.some((s) => `sample:${s.id}` === lead.uploadId)) {
    errors.uploadId = 'Unknown sample';
  }
  return errors;
}
